import React, { useEffect, useRef, useState } from "react";
import { PageSpinner, EmptyState } from "./index";

/* ---------------------------------------------------------------------------
   DataTable — consistent, interactive table chrome for admin list views.

   cols: [{ key, label, align?, width?, render?(row) }]
   rowActions(row)      → node (rendered in a sticky, always-visible column)
   busyId / busyField   → id of the row currently saving (spinner in actions)
   flashId / flashKind  → id of a row to flash ("success" | "danger") briefly
   onRowClick(row)      → optional row click handler (cursor-pointer rows)
--------------------------------------------------------------------------- */

function RowActions({ children, busy }) {
  return (
    <div className="flex items-center justify-end gap-1.5">
      {busy ? (
        <span className="inline-flex items-center gap-2 text-xs text-gray-400 pr-1">
          <span className="w-3.5 h-3.5 border-2 border-line-strong border-t-accent rounded-full animate-spin" />
          Working…
        </span>
      ) : (
        children
      )}
    </div>
  );
}

export default function DataTable({
  cols,
  rows,
  keyField = "id",
  loading = false,
  emptyTitle = "Nothing here yet",
  emptyMessage,
  emptyAction,
  rowActions,
  busyId = null,
  busyField = "id",
  flashId = null,
  flashKind = "success",
  onRowClick,
}) {
  const [flashing, setFlashing] = useState(null); // { id, kind }
  const timerRef = useRef(null);

  // Trigger the flash whenever flashId changes to a truthy value.
  useEffect(() => {
    if (!flashId) return;
    setFlashing({ id: flashId, kind: flashKind });
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setFlashing(null), 1400);
    return () => clearTimeout(timerRef.current);
  }, [flashId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="card">
        <PageSpinner />
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="card">
        <EmptyState title={emptyTitle} message={emptyMessage} action={emptyAction} />
      </div>
    );
  }

  const rowIsBusy = (row) => busyId != null && row[busyField] === busyId;
  const rowIsFlashing = (row) => flashing && row[keyField] === flashing.id;
  const clickable = typeof onRowClick === "function";

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border-separate border-spacing-0">
          <thead>
            <tr className="table-head">
              {cols.map((col) => (
                <th
                  key={col.key}
                  className={`px-3.5 py-2.5 sticky top-0 bg-surface-600 z-10 ${
                    col.align === "right" ? "text-right" : ""
                  }`}
                  style={col.width ? { width: col.width } : undefined}
                >
                  {col.label}
                </th>
              ))}
              {rowActions && (
                <th className="px-3.5 py-2.5 text-right sticky right-0 bg-surface-600 z-10 border-l border-line-strong">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const busy = rowIsBusy(row);
              const flash = rowIsFlashing(row);
              return (
                <tr
                  key={row[keyField]}
                  onClick={clickable ? () => onRowClick(row) : undefined}
                  className={`table-row group transition-colors duration-500 ${
                    clickable ? "cursor-pointer" : ""
                  } ${
                    flash
                      ? flash.kind === "danger"
                        ? "bg-accent-dim/60"
                        : "bg-emerald-900/40"
                      : ""
                  }`}
                >
                  {cols.map((col) => (
                    <td
                      key={col.key}
                      className={`px-3.5 py-2.5 align-middle border-b border-line ${
                        col.align === "right" ? "text-right" : ""
                      } ${col.tdClass || "text-gray-300"}`}
                    >
                      {col.render ? col.render(row) : row[col.key] ?? "—"}
                    </td>
                  ))}
                  {rowActions && (
                    <td className="px-3.5 py-2 text-right sticky right-0 bg-surface-850 group-hover:bg-surface-700 border-b border-l border-line group-hover:border-line transition-colors duration-500">
                      <RowActions busy={busy}>{rowActions(row)}</RowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
